import { useRef } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Camera } from 'lucide-react';
import Avatar from '@components/ui/Avatar';

const ProfileHeader = ({
  user,
  avatarSrc,
  vibesCount = 0,
  reviewsCount = 0,
  streak = 0,
  isOwnProfile,
  onUploadPhoto,
  photoBusy,
  onVibesClick,
  onReviewsClick,
}) => {
  const fileRef = useRef(null);

  const metaParts = [];
  if (user?.bio?.trim()) {
    const short = user.bio.trim().split(/[.\n]/)[0].slice(0, 40);
    if (short) metaParts.push(short);
  }
  if (user?.age != null) metaParts.push(String(user.age));
  if (user?.gender) {
    metaParts.push(user.gender.charAt(0).toUpperCase() + user.gender.slice(1));
  }

  const interests = user?.interests || [];

  const stats = [
    {
      value: vibesCount,
      label: 'Vibes',
      onClick: onVibesClick,
      clickable: Boolean(onVibesClick),
    },
    {
      value: reviewsCount,
      label: 'Reviews',
      onClick: onReviewsClick,
      clickable: Boolean(onReviewsClick),
    },
    {
      value: streak,
      label: 'Streak',
      onClick: null,
      clickable: false,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="px-1"
    >
      <div className="flex items-center gap-5">
        <div className="relative shrink-0 overflow-visible">
          <Avatar
            name={user?.name}
            src={avatarSrc}
            size="2xl"
            shape="circle"
            className="rounded-full shadow-md"
          />
          {isOwnProfile && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                className="hidden"
                onChange={onUploadPhoto}
              />
              <button
                type="button"
                disabled={photoBusy}
                onClick={() => fileRef.current?.click()}
                className="absolute -bottom-1 -right-1 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-brand-500 text-white shadow-md transition hover:bg-brand-600 active:scale-95 disabled:opacity-50"
                aria-label="Upload profile photo"
              >
                <Camera size={14} />
              </button>
            </>
          )}
        </div>

        <div className="flex min-w-0 flex-1 items-center justify-around sm:justify-start sm:gap-10 sm:pl-4">
          {stats.map((s) => {
            const Tag = s.clickable ? 'button' : 'div';
            return (
              <Tag
                key={s.label}
                type={s.clickable ? 'button' : undefined}
                onClick={s.clickable ? s.onClick : undefined}
                className={`flex flex-col items-center text-center ${
                  s.clickable
                    ? 'rounded-xl px-1 py-1 transition active:scale-95 hover:opacity-80'
                    : ''
                }`}
              >
                <p className="text-xl font-extrabold tabular-nums leading-none text-surface-900 dark:text-white sm:text-2xl">
                  {s.value}
                </p>
                <p className="mt-1.5 text-[11px] font-medium leading-none text-surface-500 dark:text-[#B5BDD1]">
                  {s.label}
                </p>
              </Tag>
            );
          })}
        </div>
      </div>

      <div className="mt-4">
        <div className="flex flex-wrap items-center gap-1.5">
          <h2 className="text-lg font-bold text-surface-900 dark:text-white">{user?.name}</h2>
          {(user?.faceVerified || user?.isVerified) && (
            <CheckCircle2
              size={18}
              className="fill-brand-500 text-white dark:fill-brand-400"
              aria-label="Verified"
            />
          )}
        </div>
        {metaParts.length > 0 && (
          <p className="mt-1 text-sm text-surface-500 dark:text-[#B5BDD1]">
            {metaParts.join(' · ')}
          </p>
        )}
        {interests.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {interests.slice(0, 8).map((interest) => (
              <span
                key={interest}
                className="rounded-xl bg-surface-100 px-3 py-1.5 text-xs font-semibold text-surface-700 dark:bg-[#1B2435] dark:text-[#B5BDD1]"
              >
                {interest}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ProfileHeader;
